// Step 1 - the customer's Cloudflare API token.
//
// The token is write-only from here. It goes up once through fdApi.setKey, is
// verified against Cloudflare before the server stores it, and never comes back
// on any response. This component holds it in state only for as long as the
// input is on screen and clears that state on success, so a verified token is
// not sitting in a React tree for the rest of the session.

import { useState } from 'react';

import { fdApi, fdErrorMessage } from '../foreignDnsApi';
import type { FdStepProps } from './shared';

const TOKEN_INPUT_ID = 'fd-cf-token';
const TOKEN_HINT_ID = 'fd-cf-token-hint';

export function StepKey({ runId, detail, reload, goToStep, toast }: FdStepProps): JSX.Element {
  const { run } = detail;
  const closed = run.status === 'completed' || run.status === 'cancelled';

  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verified, setVerified] = useState(false);

  async function verify(): Promise<void> {
    const value = token.trim();
    if (value === '') return;
    setBusy(true);
    setError(null);
    try {
      await fdApi.setKey(runId, value);
      // Cleared before anything else: from this point the browser holds no copy.
      setToken('');
      setVerified(true);
      toast('success', 'Cloudflare key verified and stored for this run.');
      await reload();
      await goToStep(2);
    } catch (err) {
      setVerified(false);
      setError(fdErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="fd-step">
      <div className="fd-step-head">
        <h2>Step 1 - Cloudflare key</h2>
        <p>
          Paste the customer's Cloudflare API token. It is verified against Cloudflare before it is
          stored, and a token that fails verification is not stored at all.
        </p>
      </div>
      <div className="fd-step-body">
        {closed ? (
          <div className="fd-notice">
            This run is {run.status}. Its key was destroyed and a closed run will not accept a new
            one. Start a new run to configure more domains.
          </div>
        ) : (
          <>
            {run.has_token && !verified && (
              <div className="fd-notice fd-notice--ok">
                A verified Cloudflare key is held for this run. You only need to enter one again if
                it was cleared, or if the customer issued a new token - for example after adding the
                <span className="fd-mono"> Zone &gt; Dynamic Redirect &gt; Edit </span>
                scope for forwarding.
              </div>
            )}

            {verified && (
              <div className="fd-notice fd-notice--ok">
                Key verified. Cloudflare accepted the token and it is stored for this run.
              </div>
            )}

            {error && <div className="fd-notice fd-notice--err">{error}</div>}

            <div className="fd-field">
              <label className="fd-field-label" htmlFor={TOKEN_INPUT_ID}>
                {run.has_token ? 'Replace the Cloudflare API token' : 'Cloudflare API token'}
              </label>
              <input
                id={TOKEN_INPUT_ID}
                aria-describedby={TOKEN_HINT_ID}
                className="fd-input fd-mono"
                type="password"
                value={token}
                maxLength={500}
                autoComplete="off"
                spellCheck={false}
                placeholder="Paste the token here"
                onChange={(e) => setToken(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void verify();
                }}
                disabled={busy}
              />
              <span className="fd-hint" id={TOKEN_HINT_ID}>
                The key is borrowed, not kept. It is held only until this run is finished or
                cancelled, is never displayed back to anyone, and is destroyed automatically if the
                run sits untouched for 24 hours. Required scopes: Zone &gt; Zone &gt; Read, Zone
                &gt; DNS &gt; Edit, and Zone &gt; Dynamic Redirect &gt; Edit when you plan to add
                forwarding.
              </span>
            </div>

            <div className="fd-actions">
              <button
                type="button"
                className="fd-btn fd-btn--primary"
                onClick={() => void verify()}
                disabled={busy || token.trim() === ''}
              >
                {busy ? 'Verifying...' : 'Verify'}
              </button>
              {run.has_token && (
                <button
                  type="button"
                  className="fd-btn fd-btn--secondary"
                  onClick={() => void goToStep(2)}
                  disabled={busy}
                >
                  Keep this key and continue
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
